import { useEffect, useState, useContext, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { LoadingContext } from '../App';
import Canvas from '../components/Canvas';
import MainFooter from '../components/MainFooter';
import blogData from '../data/blog.json';
import directoryData from '../data/blogDirectories.json';

import '../styles/blog-directory.css';

const BlogDirectoryDetail = () => {
    const { slug } = useParams<{ slug: string }>();
    const { setIsPageReady } = useContext(LoadingContext);
    const [directory, setDirectory] = useState<any>(null);
    const [relatedBlogs, setRelatedBlogs] = useState<any[]>([]);
    const [visibleCount, setVisibleCount] = useState(4);
    const blogSectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const foundDirectory = directoryData.find(d => d.slug === slug);
        if (foundDirectory) {
            setDirectory(foundDirectory);
            
            // Filter blogs that match directory tags or title
            const filtered = blogData.filter(blog => {
                const blogTags = blog.tags.map(t => t.toLowerCase());
                const blogTitle = blog.title.toLowerCase();
                const dirTitle = foundDirectory.title.toLowerCase();
                const dirId = foundDirectory.id.toLowerCase();
                const dirTags = foundDirectory.tags.map(t => t.toLowerCase());

                // Define keyword groups for broader matching
                const keywordGroups: { [key: string]: string[] } = {
                    ghl: ['ghl', 'gohighlevel', 'highlevel', 'crm', 'saas mode', 'funnels', 'snapshot', 'sub-account', 'lead nurture', 'marketing automation'],
                    aws: ['aws', 'amazon web services', 'lambda', 's3', 'serverless', 'ec2', 'cloud architecture', 'dynamodb', 'cloud computing'],
                    retell: ['retell', 'phone agent', 'conversational ai', 'voice llm', 'calling automation', 'llm agents'],
                    make: ['make.com', 'integromat', 'automation', 'webhook', 'data mapping', 'workflow integration'],
                    elevenlabs: ['elevenlabs', 'voice cloning', 'speech synthesis', 'realistic ai voice', 'tts', 'text to speech'],
                    'voice-ai': ['vapi', 'voice ai', 'ivr', 'receptionist', 'speech recognition', 'nlu', 'natural language'],
                    compliance: ['hipaa', 'gdpr', 'pci dss', 'data privacy', 'security audit', 'data residency', 'soc2', 'dpdp', 'compliance guide'],
                    'knowledge-engineering': ['rag', 'retrieval augmented generation', 'vector database', 'pinecone', 'knowledge graph', 'prompt engineering', 'expert system'],
                    vitally: ['vitally', 'customer success', 'churn', 'retention', 'health score', 'cs ops'],
                    cliniko: ['cliniko', 'practice management', 'clinic software', 'patient portal', 'medical booking'],
                    vibevoice: ['vibevoice', 'emotional ai', 'sentiment analysis', 'urgency detection'],
                    rekognition: ['rekognition', 'computer vision', 'image analysis', 'facial recognition', 'content moderation'],
                    zapier: ['zapier', 'zaps', 'zapier agents', 'zapier copilot', 'no-code', 'interfaces', 'tables', 'canvas'],
                    n8n: ['n8n', 'self-hosted', 'fair-code', 'node-based', 'technical automation', 'javascript nodes', 'workflow builder']
                };

                // 1. Check for exact tag matches or group keyword matches
                const currentGroup = keywordGroups[dirId] || [];
                const hasTagMatch = blogTags.some(tag => dirTags.includes(tag) || currentGroup.includes(tag));

                // 2. Check if any group keywords appear in the title
                const hasKeywordInTitle = currentGroup.some(kw => blogTitle.includes(kw));

                // 3. Special handling for exact platform name in title
                const hasPlatformInTitle = blogTitle.includes(dirTitle) || blogTitle.includes(dirId);
                
                // 4. Refinement for Compliance (Prevent catch-all)
                if (dirId === 'compliance') {
                    // Only include if it's specifically about compliance (has HIPAA, GDPR etc. in title/tags)
                    // and doesn't just mention "compliance" in a general sense
                    const strongComplianceKeywords = ['hipaa', 'gdpr', 'pci dss', 'data privacy', 'data residency', 'soc2', 'dpdp'];
                    const hasStrongMatch = strongComplianceKeywords.some(kw => blogTitle.includes(kw) || blogTags.includes(kw));
                    
                    // If it belongs to another major platform, it MUST have a strong compliance keyword to show here
                    const otherPlatforms = ['ghl', 'aws', 'retell', 'make', 'elevenlabs', 'cliniko', 'vitally'];
                    const belongsToOther = otherPlatforms.some(p => blogTags.includes(p) || blogTitle.includes(p));
                    
                    if (belongsToOther) {
                        return hasStrongMatch;
                    }
                    return hasTagMatch || hasKeywordInTitle || hasPlatformInTitle;
                }

                // 5. Inclusive Filter for GHL
                if (dirId === 'ghl') {
                    return hasTagMatch || hasKeywordInTitle || hasPlatformInTitle || blogTags.includes('gohighlevel');
                }

                // 6. Strict Platform Isolation for Automation Tools (Zapier, n8n, Make)
                const majorPlatforms = ['zapier', 'n8n', 'make'];
                const normalizedDirId = dirId.includes('make') ? 'make' : (dirId.includes('zapier') ? 'zapier' : (dirId.includes('n8n') ? 'n8n' : dirId));
                
                if (majorPlatforms.includes(normalizedDirId)) {
                    const otherPlatforms = majorPlatforms.filter(p => p !== normalizedDirId);
                    const mentionsOther = otherPlatforms.some(p => blogTitle.includes(p) || blogTags.includes(p));
                    const mentionsCurrent = blogTitle.includes(normalizedDirId) || blogTitle.includes(dirTitle) || blogTags.includes(normalizedDirId);
                    
                    // If it specifically mentions another major platform and NOT the current one, exclude it
                    // This prevents n8n blogs from showing up in Zapier just because of 'Automation' tag
                    if (mentionsOther && !mentionsCurrent) {
                        return false;
                    }
                }

                return hasTagMatch || hasKeywordInTitle || hasPlatformInTitle;
            });
            
            // Sort by date descending
            const sorted = filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
            setRelatedBlogs(sorted);
            
            setIsPageReady(true);
        } else {
            setIsPageReady(true);
        }
        
        // Reset visible count on slug change
        setVisibleCount(4);
    }, [slug, setIsPageReady]);

    const handleLoadMore = (e: React.MouseEvent) => {
        e.preventDefault();
        if (visibleCount >= relatedBlogs.length) {
            // Read Less logic
            setVisibleCount(4);
            if (blogSectionRef.current) {
                blogSectionRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        } else {
            setVisibleCount(prev => prev + 4);
        }

        // Refresh GSAP scroll triggers after the new blogs are rendered
        setTimeout(() => {
            if (window.ScrollTrigger) {
                window.ScrollTrigger.refresh();
            }
        }, 200);
    };

    if (!directory) {
        return <div className="loading-screen" style={{ backgroundColor: '#fff', color: '#121212', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading Directory...</div>;
    }

    return (
        <>
            <Helmet>
                <title>{directory.meta?.title || directory.seo?.title || `${directory.title} | Sacesta`}</title>
                <meta name="description" content={directory.meta?.description || directory.seo?.description || directory.description} />
                <meta name="keywords" content={directory.meta?.keywords || directory.seo?.keywords} />
                <link rel="canonical" href={`https://sacesta.com/our-work/blog/directory/${directory.slug}`} />
            </Helmet>
            <div className="cursor1"></div>
            <div className="cursor2"></div>

            <div className="has-smooth" id="has_smooth"></div>

            <Canvas />

            <button id="scroll_top" className="scroll-top"><i className="fa-solid fa-arrow-up"></i></button>

            <header className="header__area-3">
                <div className="header__inner-3">
                    <div className="header__logo-2">
                        <Link to="/" className="logo-dark">
                            <img src="/assets/imgs/logo/sacesta-logo-black.png" style={{ width: '150px', height: '40px' }} alt="Site Logo" />
                        </Link>
                        <Link to="/" className="logo-light">
                            <img src="/assets/imgs/logo/site-logo-white-2.png" alt="Site Logo" />
                        </Link>
                    </div>
                    <div className="header__nav-2">
                        <ul className="main-menu-3 menu-anim">
                            <li><Link to="/">Home</Link></li>
                            <li><Link to="/about">About</Link></li>
                            <li><Link to="/our-work">Our Work</Link></li>
                            <li><a href="https://calendly.com/sacesta/30min">Book A Meet</a></li>
                        </ul>
                    </div>
                    <div className="header__nav-icon-3">
                        <button id="open_offcanvas"><img src="/assets/imgs/icon/menu-black.png" alt="Menubar Icon" /></button>
                    </div>
                </div>
            </header>

            <div id="smooth-wrapper">
                <div id="smooth-content">
                    <main className="blog-directory-page">
                        {/* Hero Section */}
                        <section className="directory-hero">
                            <div className="container">
                                <h1 className="headline">{directory.headline || directory.hero?.headline}</h1>
                                <p className="subtext">{directory.description || directory.hero?.subtext}</p>
                                <div className="hero-actions">
                                    <a href="https://calendly.com/sacesta/30min" className="btn-primary">Book a Discovery Call</a>
                                </div>
                            </div>
                        </section>

                        {/* Process Section */}
                        <section className="directory-process">
                            <div className="container">
                                <h2 className="section-title">Our Approach</h2>
                                <div className="process-grid">
                                    {directory.process.map((step: any, i: number) => (
                                        <div key={i} className="process-card">
                                            <span className="num">{step.step || step.num}</span>
                                            <h3>{step.title}</h3>
                                            <p>{step.desc}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>

                        {/* Integrations Section */}
                        <section className="directory-integrations">
                            <div className="container">
                                <h2 className="section-title">Common Integrations</h2>
                                <div className="integrations-grid">
                                    {directory.integrations.map((item: string, i: number) => (
                                        <div key={i} className="integration-chip">
                                            <i className="fa-solid fa-circle-check" style={{ color: '#121212', fontSize: '14px' }}></i>
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>

                        {/* FAQ Section */}
                        <section className="directory-faq">
                            <div className="container">
                                <div className="faq-layout">
                                    <div>
                                        <h2 className="section-title">Questions clients ask before we start.</h2>
                                    </div>
                                    <div className="faq-list">
                                        {directory.faqs.map((faq: any, i: number) => (
                                            <div key={i} className="faq-item">
                                                <h4>{faq.q} <i className="fa-solid fa-plus" style={{ fontSize: '14px' }}></i></h4>
                                                <p>{faq.a}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Blog Grid Section */}
                        <section className="directory-blogs" ref={blogSectionRef}>
                            <div className="container">
                                <h2 className="section-title">Notes, recipes & lessons from real builds</h2>
                                <div className="blog-grid">
                                    {relatedBlogs.slice(0, visibleCount).map((blog, i) => (
                                        <Link key={i} to={`/our-work/blog/${blog.slug}`} className="blog-card">
                                            <div className="blog-card-image">
                                                <img src={`/${blog.thumbnail.image}`} alt={blog.thumbnail.alt} />
                                            </div>
                                            <div className="blog-card-content">
                                                <div className="blog-card-meta">{blog.date}</div>
                                                <h3>{blog.title}</h3>
                                                <p>{blog.excerpt}</p>
                                                <div className="blog-card-link" style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    Read More <i className="fa-solid fa-arrow-right"></i>
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>

                                {relatedBlogs.length > 4 && (
                                    <div className="load-more-container">
                                        <button className="btn-load-more" onClick={(e) => handleLoadMore(e)}>
                                            {visibleCount >= relatedBlogs.length ? 'Read Less' : 'Read More'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* Final CTA Button */}
                        <div className="blog-contact-btn-wrapper">
                            <a 
                                href="https://calendly.com/sacesta/30min" 
                                className="contact-rect-btn"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Contact for {directory.title} Consultation
                                <i className="fa-solid fa-arrow-right"></i>
                            </a>
                        </div>
                    </main>

                    <MainFooter />
                </div>
            </div>
        </>
    );
};

export default BlogDirectoryDetail;
